/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable simple-import-sort/imports */
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SortedDataGridPro6 from "../../../../design/sorted-datagrid-pro-6";
import CrossSkillLegends from "../../../cross-skilling/ui/cross-skill-header/ui/cross-skill-ledgends";
import SkeletonLoadingOverlay from "../../../../utils/use-skeleton-loading-overlay";
import useTableHeight from "../../../../utils/use-table-height";
import Highlight from "../../../Highlight";
import { openSkillInsightDetails } from "../../slices/skill-insight-report-slice";
import {
	InterestedWorkersCount,
	SkillInsightModel,
	WorkersSkillLevel, WorkersSkillLevelCount
} from "../../slices/insight-report-types";

const SkillInsightReportTable = ({
	selectedKPI,
	skillsReportData,
	searchText,
	fetchSkillInsightReportStatus,
	allSkillLevels,
	overAllColumns,
	dispatch,
	allLocations,
	priorities,
	selectedLocations,
  height,
}: any) => {
	const { t } = useTranslation();
	const columns = useMemo(() => {
		const col: any = [];

		const isColumnVisible = (field: string) =>
			overAllColumns.find(
				(item: { field: string; checked: boolean }) =>
					item.field === field && item.checked
			);

		// Skill Name column
		if (isColumnVisible("skillName")) {
			col.push({
				field: "skillName",
				headerName: t("insight-dashboard_skill-name"),
				minWidth: 160,
				flex: 1,
				// align: "left",
				// headerAlign: "center",
				renderCell: (params: any) => (
					<Tooltip title={params.row.skillName}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
								marginLeft: "12px",
							}}
						>
							<Highlight searchText={searchText} value={params.row.skillName} />
						</span>
					</Tooltip>
				),
				renderHeader: () => (
					<Tooltip title={t("insight-dashboard_skill-name")}>
						<span style={{ padding: "0px 12px", fontWeight: "bold" }}>
							{t("insight-dashboard_skill-name")}
						</span>
					</Tooltip>
				),
			});
		}

		// Skill Priority column
		if (isColumnVisible("skillPriority")) {
			col.push({
				field: "skillPriority",
				headerName: t("insight-dashboard_skill-priority"),
				minWidth: 160,
				flex: 1,
				align: "center",
				headerAlign: "center",
				renderCell: (params: any) => (
					<Tooltip title={params.row.skillPriority}>
						<span
							style={{
								textOverflow: "ellipsis",
								overflow: "hidden",
								marginLeft: "12px",
							}}
						>
							<Highlight
								searchText={searchText}
								value={params.row.skillPriority}
							/>
						</span>
					</Tooltip>
				),
				renderHeader: () => (
					<Tooltip title={t("insight-dashboard_skill-priority")}>
						<span style={{ padding: "0px 12px", fontWeight: "bold" }}>
							{t("insight-dashboard_skill-priority")}
						</span>
					</Tooltip>
				),
			});
		}

		// Interested workers count column
		if (isColumnVisible("interestedWorkersCount")) {
			col.push({
				field: "interestedWorkersCount",
				headerName: t("insight-dashboard_interested-workers"),
				minWidth: 205,
				flex: 1,
				align: "center",
				headerAlign: "center",
				renderHeader: () => (
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<span style={{ padding: "0px 6px", fontWeight: "bold" }}>
							{t("insight-dashboard_interested-workers")}
						</span>
						<Tooltip title={t("insight-dashboard_interested-workers-tooltip")}>
							<InfoOutlinedIcon sx={{ fontSize: "18px", color: "#6c6d6e" }} />
						</Tooltip>
					</Box>
				)
			});
		}

		// Worker count column
		if (isColumnVisible("totalWorkers")) {
			col.push({
				field: "totalWorkers",
				headerName: t("insight-dashboard_total-workers"),
				minWidth: 190,
				flex: 1,
				align: "center",
				headerAlign: "center",
				renderHeader: () => (
					<Box sx={{ display: "flex", alignItems: "center" }}>
						<span style={{ padding: "0px 6px", fontWeight: "bold" }}>
							{t("insight-dashboard_workers-with-skill")}
						</span>
						<Tooltip title={t("insight-dashboard_workers-with-skill-tooltip")}>
							<InfoOutlinedIcon sx={{ fontSize: "18px", color: "#6c6d6e" }} />
						</Tooltip>
					</Box>
				),
			});
		}

		// Dynamic skill levels columns
		if (
			allSkillLevels?.length > 0 &&
			selectedKPI !== "interestedWorkersKPI" &&
			selectedKPI !== "interestedWorkers"
		) {
			allSkillLevels.forEach((skillLevel: any) => {
				col.push({
					field: `workersCountAtL${skillLevel.level}`,
					headerName: skillLevel.name,
					minWidth: 135,
					flex: 1,
					align: "center",
					headerAlign: "center",
					renderHeader: () => (
						<Box style={{ fontWeight: "bold" }}>
							<CrossSkillLegends
								isSkillLevelShownInTableHeader={true}
								allSkillLevels={[allSkillLevels[skillLevel.level]]}
							/>
						</Box>
					),
					renderCell: (params: any) =>
						params.row[`workersCountAtL${skillLevel.level}`] || "-",
				});
			});
		}

		return col;
	}, [allSkillLevels, selectedKPI, overAllColumns, searchText, t]);

	const rows = useMemo(() => {
		try {
			return skillsReportData
				.map((item: SkillInsightModel, index: number) => {
					const workersCounts: any = {};
					let workersCoverage = 0;

					item.workersSkillLevel.forEach((workerSkillLevel: WorkersSkillLevel) => {
						const locationFilterNotApplied = selectedLocations ? selectedLocations.length === 0 : true;
						if (locationFilterNotApplied || selectedLocations?.includes(workerSkillLevel.locationId)) {
							workerSkillLevel.workerCount.forEach((skill: WorkersSkillLevelCount) => {
								if (skill.level !== undefined && skill.count !== undefined) {
									const originalCount = workersCounts[`workersCountAtL${skill.level}`] || 0;
									workersCounts[`workersCountAtL${skill.level}`] = originalCount + skill.count;
									const lev = allSkillLevels.find((x: any) => x.level === skill.level);
									if (lev && lev.isQualifiedAsSkilled) {
										workersCoverage += skill.count;
									}
								}
							});
						}
					});

					const priority = priorities?.[item.priorityId]
						? `${priorities[item.priorityId].code} (${
							priorities[item.priorityId].name
						})`
						: "-";

					const getInterestedWorkersCount = () => {
						try {
							let count = 0;
							const interestedWorkersCount = item.interestedWorkersCount;
							interestedWorkersCount.forEach((interestedWorkerCount: InterestedWorkersCount) => {
								if (selectedLocations && selectedLocations.length) {
									// If filter is applied
									if (selectedLocations.includes(interestedWorkerCount.locationId)) {
										count += interestedWorkerCount.count;
									}
								} else {
									count += interestedWorkerCount.count;
								}
							});
							return count;
						} catch (e) {
							console.error("Get interested worker error", e);
							return 0;
						}
					};

					return {
						id: index,
						skillName: item.skillName.trim(),
						...workersCounts,
						interestedWorkersCount: getInterestedWorkersCount(),
						totalWorkers: workersCoverage,
						skillId: item.skillId,
						skillPriority: priority,
					};
				})
				.sort((a: any, b: any) => a.skillName.localeCompare(b.skillName));
		} catch (e) {
			console.log("Skill Insight table error", e);
		}
	}, [skillsReportData, priorities, allSkillLevels, selectedLocations]);

	const handleRowClick = (params: any) => {
		dispatch(
			openSkillInsightDetails({
				skillId: params.row.skillId,
				locations: allLocations,
				selectedKPI: selectedKPI,
			})
		);
	};

	return (
    <Box
      style={{
        overflow: "hidden",
        width: "100%",
      }}
    >
      <SortedDataGridPro6
        rows={rows || []}
        columns={columns}
        onRowClick={handleRowClick}
        experimentalFeatures={{ columnGrouping: true }}
        loading={fetchSkillInsightReportStatus === "pending"}
        defaultSortField="skillName"
        slots={{
          loadingOverlay: () => (
            <SkeletonLoadingOverlay
              columnData={{
                columnName: "skillName",
                columnStyle: { marginLeft: "12px" },
              }}
            />
          ),
        }}
        disableColumnFilter
        disableColumnMenu
        pagination
        disableColumnSelector
        disableDensitySelector
        disableColumnReorder
        sx={{
          overflow: "hidden",
          height: height,
          border: "none !important",
          "& .MuiDataGrid-row": {
            cursor: "pointer",
          },
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
        }}
      />
    </Box>
  );
};

export default SkillInsightReportTable;
